type ColumnValueLike = {
  id?: string;
  type?: string;
  linkedItemIds?: string[];
  linked_item_ids?: string[];
  linkedItems?: unknown[];
  linked_items?: unknown[];
  displayValue?: string;
  display_value?: string;
  text?: string;
};

type ItemLike = {
  column_values?: ColumnValueLike[];
  columnValues?: ColumnValueLike[];
};

export function getItemColumnValues<T extends ColumnValueLike>(
  item: ItemLike | null | undefined,
): T[] {
  return (item?.column_values ?? item?.columnValues ?? []) as T[];
}

export function normalizeItemColumnValues<T extends ItemLike>(item: T): T {
  const columns = getItemColumnValues(item);
  if (item.column_values === columns) return item;
  return { ...item, column_values: columns };
}

export function columnHasLinkedItems(
  column: ColumnValueLike | null | undefined,
): boolean {
  const ids = column?.linkedItemIds ?? column?.linked_item_ids ?? [];
  const items = column?.linkedItems ?? column?.linked_items ?? [];
  return ids.length > 0 || items.length > 0;
}

export function emptyBoardRelationColumnIds(
  item: ItemLike | null | undefined,
): string[] {
  return getItemColumnValues(item)
    .filter(column => column?.type === 'board_relation' && column.id)
    .filter(column => !columnHasLinkedItems(column))
    .map(column => String(column.id));
}

/** Item-view must keep the opened item even if a later board row has links. */
export function applyConnectBoardSample<T extends ItemLike>(
  item: T | undefined,
  isSampleContext: boolean,
  samplesByColumnId: Record<string, ItemLike | null | undefined>,
): T | undefined {
  if (!item || !isSampleContext) return item;
  return mergeSampleRelationColumns(item, samplesByColumnId);
}

export function mergeSampleRelationColumns<T extends ItemLike>(
  baseItem: T,
  samplesByColumnId: Record<string, ItemLike | null | undefined>,
): T {
  const baseColumns = getItemColumnValues(baseItem);
  if (baseColumns.length === 0) return baseItem;

  let changed = false;
  const nextColumns = baseColumns.map(column => {
    if (!column.id || columnHasLinkedItems(column)) return column;
    const sample = samplesByColumnId[column.id];
    const sampleColumn = getItemColumnValues(sample).find(
      candidate => candidate.id === column.id,
    );
    if (!sampleColumn || !columnHasLinkedItems(sampleColumn)) return column;

    changed = true;
    return {
      ...column,
      linked_items: sampleColumn.linked_items ?? sampleColumn.linkedItems,
      linkedItems: sampleColumn.linkedItems ?? sampleColumn.linked_items,
      linked_item_ids: sampleColumn.linked_item_ids ?? sampleColumn.linkedItemIds,
      linkedItemIds: sampleColumn.linkedItemIds ?? sampleColumn.linked_item_ids,
      display_value:
        sampleColumn.display_value ??
        sampleColumn.displayValue ??
        column.display_value,
      displayValue:
        sampleColumn.displayValue ??
        sampleColumn.display_value ??
        column.displayValue,
      text: sampleColumn.text ?? column.text,
    };
  });

  if (!changed) return baseItem;
  return {
    ...baseItem,
    column_values: nextColumns,
  };
}
